import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactFlow, Background, Handle, Position, useReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { api, fetchFilterOptions } from '../lib/api';
import { LeadEditor } from './LeadPage';
import { useAuth } from '../lib/auth';
import B2cChat from '../components/B2cChat';
import Mappa from './Mappa';

// === Icons (lucide-style) ======================================================
const Icon = ({ size = 18, stroke = 1.75, children }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IWA = (p) => (<Icon {...p}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></Icon>);
const IUsers = (p) => (<Icon {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Icon>);
const IInbox = (p) => (<Icon {...p}><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></Icon>);
const IMicroscope = (p) => (<Icon {...p}><path d="M6 18h8" /><path d="M3 22h18" /><path d="M14 22a7 7 0 1 0 0-14h-1" /><path d="M9 14h2" /><path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2Z" /><path d="M12 6V3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3" /></Icon>);
const ISend = (p) => (<Icon {...p}><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></Icon>);
const ICheck = (p) => (<Icon {...p}><polyline points="20 6 9 17 4 12" /></Icon>);
const IArrow = (p) => (<Icon {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></Icon>);
const ISparkle = (p) => (<Icon {...p}><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" /></Icon>);

const COLORS = {
  primary: 'oklch(0.45 0.18 250)',
  primaryLight: 'oklch(0.96 0.04 250)',
  success: '#16a34a',
  successLight: 'oklch(0.96 0.05 150)',
  successBorder: 'oklch(0.78 0.13 150)',
};

const FLOW_ICONS = {
  whatsapp: IWA,
  lead: IUsers,
  match: IInbox,
  analisi: IMicroscope,
  proposta: ISend,
};

// === Custom Node ================================================================
function StepNode({ data }) {
  const { number, label, desc, status, isAuto, link, action, onActivate, cardWidth, onMeasure } = data;
  // Cliccabile se c'è un link o un'action
  const clickable = !!(link || action);
  const Ic = FLOW_ICONS[data.key] || ISparkle;
  const isDone = status === 'done';
  const isCurrent = status === 'current';

  const cardBg = isDone ? COLORS.successLight : (isCurrent ? '#fff' : 'var(--bg-elev)');
  const cardBorder = isDone ? COLORS.successBorder : (isCurrent ? COLORS.primary : 'var(--border)');
  const cardShadow = isCurrent
    ? `0 0 0 4px ${COLORS.primaryLight}, 0 20px 40px -12px oklch(0.45 0.18 250 / 0.35), 0 8px 20px -6px oklch(0.45 0.18 250 / 0.2)`
    : (isDone ? '0 4px 12px -4px oklch(0.65 0.18 150 / 0.25)' : '0 2px 4px oklch(0 0 0 / 0.04)');

  const iconBg = isDone ? COLORS.success : (isCurrent ? COLORS.primary : 'oklch(0.94 0 0)');
  const iconColor = isDone || isCurrent ? '#fff' : 'var(--fg-muted)';

  // ResizeObserver: misura l'altezza reale di QUESTO nodo e la riporta al parent,
  // che ricalcola le y di tutti i nodi per evitare overlap su mobile (dove il
  // testo wrappa e le card crescono in altezza imprevedibilmente).
  // IMPORTANTE: uso offsetHeight (border-box completo), NON entry.contentRect.height
  // (che esclude padding+border → causa overlap di ~14-38px).
  const cardRef = useRef(null);
  useEffect(() => {
    if (!cardRef.current || !onMeasure) return;
    const el = cardRef.current;
    const ro = new ResizeObserver(() => {
      onMeasure(data.key, el.offsetHeight);
    });
    ro.observe(el);
    // Misura iniziale subito
    onMeasure(data.key, el.offsetHeight);
    return () => ro.disconnect();
  }, [data.key, onMeasure]);

  // Layout: card a COLONNA — header row (icona + titolo) sopra, descrizione e CTA
  // a tutta larghezza sotto (così il testo non lascia spazio vuoto sotto l'icona).
  return (
    <div
      ref={cardRef}
      onClick={() => clickable && onActivate?.(data)}
      style={{
        position: 'relative',
        width: cardWidth,
        background: cardBg,
        border: `${isCurrent ? '2px' : '1px'} solid ${cardBorder}`,
        borderRadius: 16,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        cursor: clickable ? 'pointer' : 'default',
        boxShadow: cardShadow,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        fontFamily: 'inherit',
        // Forza pointer-events: React Flow setta pe:none sul .react-flow__node parent
        // quando disabilito drag/connect/select, e altrimenti i click passano attraverso
        // al .react-flow__pane sottostante senza mai raggiungere il mio onClick.
        pointerEvents: 'all',
      }}
      onMouseEnter={(e) => { if (clickable) e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { if (clickable) e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0, pointerEvents: 'none' }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, pointerEvents: 'none' }} />

      {/* HEADER ROW: icona + titolo + badge auto */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 11,
          background: iconBg, color: iconColor,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, position: 'relative',
        }}>
          <Ic size={20} />
          {isDone && (
            <span style={{
              position: 'absolute', top: -5, right: -5,
              width: 18, height: 18, borderRadius: '50%',
              background: COLORS.success, color: '#fff',
              border: `2px solid ${cardBg}`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}><ICheck size={10} stroke={3} /></span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg)', lineHeight: 1.25 }}>
            {label}
          </div>
          {isAuto && (
            <span style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
              color: COLORS.primary, background: COLORS.primaryLight,
              padding: '2px 6px', borderRadius: 4,
            }}>Auto</span>
          )}
        </div>
      </div>

      {/* DESCRIZIONE A TUTTA LARGHEZZA */}
      <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45 }}>
        {desc}
      </div>

      {/* CTA solo per il current — inline a sinistra */}
      {isCurrent && clickable && (
        <div style={{
          marginTop: 2,
          display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
          padding: '6px 12px', background: COLORS.primary, color: '#fff',
          borderRadius: 8, fontSize: 12, fontWeight: 600,
          boxShadow: '0 2px 6px -2px oklch(0.45 0.18 250 / 0.5)',
        }}>
          Inizia ora <IArrow size={12} />
        </div>
      )}
    </div>
  );
}

const nodeTypes = { step: StepNode };

// === Flow ======================================================================
// Altezze approssimative dei nodi (servono per posizionarli senza overlap):
// - "current" è più alto perché ha il bottone "Inizia ora"
// - "done"/"pending" sono compatti
const NODE_H_NORMAL = 110;
const NODE_H_CURRENT = 165;
const GAP = 24;

function OnboardingFlowInner({ onboarding, onActivate, onHeight, cardWidth }) {
  const { steps } = onboarding;
  const currentIdx = steps.findIndex((s) => !s.done);
  const reactFlow = useReactFlow();

  // onActivate riceve l'intero step (così può decidere se link → navigate, action → open modal, ecc.)
  const handleActivate = useCallback((step) => onActivate?.(step), [onActivate]);

  // Heights misurate via ResizeObserver da ogni StepNode.
  // Inizializzo con fallback stime (servono solo per il PRIMO render, poi i nodi
  // si auto-misurano e ricalcoliamo le y reali).
  const [heights, setHeights] = useState({});
  const handleMeasure = useCallback((key, h) => {
    setHeights((prev) => prev[key] === h ? prev : { ...prev, [key]: h });
  }, []);

  // Calcolo y dinamicamente con altezze REALI (fallback su stima se non ancora misurato)
  const { nodes, totalHeight } = useMemo(() => {
    let y = 0;
    const out = steps.map((s, i) => {
      const status = s.done ? 'done' : (i === currentIdx ? 'current' : 'pending');
      const node = {
        id: s.key,
        type: 'step',
        position: { x: 0, y },
        data: {
          ...s, number: i + 1, status, isAuto: !!s.auto,
          onActivate: handleActivate, cardWidth, onMeasure: handleMeasure,
        },
        draggable: false,
        selectable: false,
      };
      const measured = heights[s.key];
      const fallback = status === 'current' ? NODE_H_CURRENT : NODE_H_NORMAL;
      const h = measured || fallback;
      y += h + GAP;
      return node;
    });
    return { nodes: out, totalHeight: y - GAP };
  }, [steps, currentIdx, handleActivate, cardWidth, heights, handleMeasure]);

  // Comunica l'altezza esatta al parent così il container è tagliato preciso (no whitespace).
  useEffect(() => { onHeight?.(totalHeight); }, [totalHeight, onHeight]);

  const edges = useMemo(() => {
    const e = [];
    for (let i = 0; i < steps.length - 1; i++) {
      const fromDone = steps[i].done;
      const toCurrent = i + 1 === currentIdx;
      const isActive = fromDone && toCurrent;
      const isCompleted = fromDone && steps[i + 1].done;

      e.push({
        id: `e-${steps[i].key}-${steps[i + 1].key}`,
        source: steps[i].key,
        target: steps[i + 1].key,
        animated: isActive,
        type: 'smoothstep',
        style: {
          stroke: isCompleted ? COLORS.success : (isActive ? COLORS.primary : 'oklch(0.85 0 0)'),
          strokeWidth: isActive ? 3 : (isCompleted ? 2.5 : 2),
          strokeDasharray: !isActive && !isCompleted ? '6 4' : undefined,
        },
      });
    }
    return e;
  }, [steps, currentIdx]);

  // Re-fit: padding piccolo + zoom fissato a 1 così le card mantengono dimensioni reali
  // e l'altezza del container coincide con la vera estensione del flusso (no whitespace).
  useEffect(() => {
    const t = setTimeout(() => {
      reactFlow.fitView({ padding: 0.02, duration: 300, minZoom: 1, maxZoom: 1 });
    }, 50);
    return () => clearTimeout(t);
  }, [nodes, reactFlow]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      nodeOrigin={[0.5, 0]}
      proOptions={{ hideAttribution: true }}
      panOnDrag={false}
      panOnScroll={false}
      zoomOnScroll={false}
      zoomOnPinch={false}
      zoomOnDoubleClick={false}
      preventScrolling={false}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      fitView
      fitViewOptions={{ padding: 0.02, minZoom: 1, maxZoom: 1 }}
      minZoom={1}
      maxZoom={1}
      style={{ background: 'transparent' }}
    >
      <Background variant="dots" gap={20} size={1.4} color="oklch(0.88 0 0)" />
    </ReactFlow>
  );
}

function OnboardingFlow({ onboarding, onActivate }) {
  const { done_count, total_count, complete } = onboarding;
  const [flowHeight, setFlowHeight] = useState(600);
  const [cardWidth, setCardWidth] = useState(380);
  const containerRef = useRef(null);

  // ResizeObserver: misura la VERA larghezza del container React Flow
  // e calcola la cardWidth ottimale (max 380, con 24px di margine ai bordi).
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      setCardWidth(Math.max(220, Math.min(380, w - 24)));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (complete) {
    return (
      <div style={{
        background: 'var(--bg-elev)', border: '1px solid var(--border)',
        borderRadius: 16, padding: 48, textAlign: 'center',
      }}>
        <div style={{
          width: 64, height: 64, margin: '0 auto 16px', borderRadius: '50%',
          background: COLORS.successLight, color: COLORS.success,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}><ICheck size={32} stroke={2.5} /></div>
        <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>
          Tutto pronto.
        </div>
        <div style={{ fontSize: 14, color: 'var(--fg-muted)' }}>
          Il tuo workspace è configurato. Usa la sidebar per navigare tra i tuoi Match, Lead e Trattative.
        </div>
      </div>
    );
  }

  const currentStep = onboarding.steps[onboarding.steps.findIndex((s) => !s.done)];

  return (
    <div style={{
      background: 'var(--bg-elev)',
      border: '1px solid var(--border)',
      borderRadius: 16,
      padding: 24,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 8, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
            Il tuo workflow Rivelasta
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--fg)', letterSpacing: -0.3 }}>
            Prossimo passo: <span style={{ color: COLORS.primary }}>{currentStep?.label}</span>
          </div>
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', background: 'var(--bg)',
          border: '1px solid var(--border)', borderRadius: 999,
          fontSize: 12, fontWeight: 500, color: 'var(--fg-muted)',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS.primary }} />
          {done_count} / {total_count} completati
        </div>
      </div>

      {/* Diagramma React Flow — altezza dinamica calcolata dal child, width misurata via ResizeObserver */}
      <div ref={containerRef} style={{ height: flowHeight + 24, marginTop: 8 }}>
        <ReactFlowProvider>
          <OnboardingFlowInner
            onboarding={onboarding}
            onActivate={onActivate}
            onHeight={setFlowHeight}
            cardWidth={cardWidth}
          />
        </ReactFlowProvider>
      </div>
    </div>
  );
}

// === Main page =================================================================

export default function Dashboard() {
  const { user } = useAuth();
  // Per gli account B2C: split layout chat (sinistra) + Mappa (destra).
  // La chat fa eseguire azioni (apply_filter via search params) e la Mappa,
  // che legge useSearchParams(), si aggiorna automaticamente.
  if (user?.is_b2c) {
    return (
      <div className="b2c-dash">
        <div className="b2c-dash__map"><Mappa /></div>
        <div className="b2c-dash__chat"><B2cChat /></div>
      </div>
    );
  }

  return <DashboardB2b />;
}

function DashboardB2b() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [refreshTick, setRefreshTick] = useState(0);
  const navigate = useNavigate();

  // Carica filterOptions una volta sola (servono al LeadEditor per zone/comune)
  useEffect(() => {
    fetchFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const res = await api.get('/api/dashboard/today');
        if (alive) { setData(res.data); setError(null); }
      } catch (e) {
        if (alive) setError(e.message || 'Errore caricamento');
      } finally {
        if (alive) setLoading(false);
      }
    };
    tick();
    const id = setInterval(tick, 15_000);
    return () => { alive = false; clearInterval(id); };
  }, [refreshTick]);

  // Dispatcher per i click sui nodi del workflow
  const handleStepActivate = (step) => {
    if (step.action === 'open-lead-modal') {
      setLeadModalOpen(true);
      return;
    }
    if (step.link) navigate(step.link);
  };

  const handleLeadSaved = () => {
    setLeadModalOpen(false);
    setRefreshTick((t) => t + 1); // ri-fetch dashboard data → onboarding step "lead" diventa done
    // Notifica la Sidebar (e qualunque altro listener) che lo stato è cambiato:
    // così la sidebar fa il refetch immediato senza aspettare il polling da 10s,
    // e l'animazione di sblocco parte subito dopo il save.
    window.dispatchEvent(new CustomEvent('rivelasta:dashboard-changed'));
  };

  if (loading) {
    return (
      <main className="content" style={{ padding: 32 }}>
        <div style={{
          height: 760, background: 'var(--bg-elev)',
          border: '1px solid var(--border)', borderRadius: 16,
          opacity: 0.4, animation: 'pulse 1.4s ease-in-out infinite',
        }} />
        <style>{`@keyframes pulse { 0%,100%{opacity:0.35} 50%{opacity:0.6} }`}</style>
      </main>
    );
  }

  if (error) {
    return (
      <main className="content" style={{ padding: 32 }}>
        <div style={{
          background: 'oklch(0.96 0.04 25)', border: '1px solid oklch(0.75 0.12 25)',
          borderRadius: 8, padding: 16, color: 'oklch(0.35 0.15 25)', fontSize: 13,
        }}>
          Errore: {error}
        </div>
      </main>
    );
  }

  const { onboarding } = data;

  return (
    <main className="content" style={{ padding: 'clamp(16px, 3vw, 32px)', overflowY: 'auto' }}>
      <OnboardingFlow onboarding={onboarding} onActivate={handleStepActivate} />

      {leadModalOpen && (
        <LeadEditor
          lead={null}
          onClose={() => setLeadModalOpen(false)}
          onSaved={handleLeadSaved}
          filterOptions={filterOptions}
        />
      )}
    </main>
  );
}
